import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock,
  LucideIcon,
  Moon,
  Pause,
  Power,
  StopCircle,
  Trash2,
  XCircle,
} from "lucide-react";
import { DeploymentStatus, ServiceStatus } from "../types";

export type StatusTone =
  | "positive"
  | "warning"
  | "negative"
  | "neutral"
  | "info";

export interface StatusMeta {
  label: string;
  icon: LucideIcon;
  tone: StatusTone;
  /** Whether the status is in-flight and should pulse */
  pulse?: boolean;
  /** Hint for users in tooltips */
  description?: string;
}

const tone = {
  positive: "bg-success/15 text-success border-success/20",
  warning:
    "bg-warning/15 text-warning-foreground border-warning/30 dark:text-warning",
  negative: "bg-destructive/15 text-destructive border-destructive/20",
  info: "bg-info/15 text-info border-info/20",
  neutral: "bg-muted text-muted-foreground border-border",
} as const;

export function toneClasses(t: StatusTone): string {
  return tone[t];
}

const dotTone = {
  positive: "bg-success",
  warning: "bg-warning",
  negative: "bg-destructive",
  info: "bg-info",
  neutral: "bg-muted-foreground",
} as const;

export function dotToneClass(t: StatusTone): string {
  return dotTone[t];
}

export const deploymentStatusMeta: Record<DeploymentStatus, StatusMeta> = {
  queued: {
    label: "Queued",
    icon: Clock,
    tone: "neutral",
    description: "Waiting for an available builder",
  },
  building: {
    label: "Building",
    icon: Activity,
    tone: "info",
    pulse: true,
    description: "Building the application image",
  },
  deploying: {
    label: "Deploying",
    icon: Activity,
    tone: "info",
    pulse: true,
    description: "Rolling out to the cluster",
  },
  running: {
    label: "Running",
    icon: CheckCircle2,
    tone: "positive",
    description: "Healthy and serving traffic",
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    tone: "negative",
    description: "The deployment did not succeed",
  },
  cancelled: {
    label: "Cancelled",
    icon: StopCircle,
    tone: "neutral",
    description: "Cancelled before completion",
  },
  stopped: {
    label: "Stopped",
    icon: Power,
    tone: "neutral",
    description: "Stopped by a user",
  },
};

export const serviceStatusMeta: Record<ServiceStatus, StatusMeta> = {
  pending: {
    label: "Pending",
    icon: Clock,
    tone: "neutral",
    description: "Service is being prepared",
  },
  building: {
    label: "Building",
    icon: Activity,
    tone: "info",
    pulse: true,
  },
  running: {
    label: "Running",
    icon: CheckCircle2,
    tone: "positive",
  },
  stopped: {
    label: "Stopped",
    icon: Pause,
    tone: "neutral",
  },
  failed: {
    label: "Failed",
    icon: AlertCircle,
    tone: "negative",
  },
  degraded: {
    label: "Degraded",
    icon: AlertTriangle,
    tone: "warning",
    description: "Running but unhealthy or partially degraded",
  },
  deleting: {
    label: "Deleting",
    icon: Trash2,
    tone: "neutral",
    pulse: true,
    description: "Deletion in progress",
  },
  "delete-stuck": {
    label: "Delete Stuck",
    icon: AlertTriangle,
    tone: "negative",
    description: "Deletion operation is stuck — manual intervention may be needed",
  },
  sleeping: {
    label: "Sleeping",
    icon: Moon,
    tone: "neutral",
    description: "Auto-scaled to zero",
  },
  archived: {
    label: "Archived",
    icon: Archive,
    tone: "neutral",
  },
};

export function getDeploymentStatusMeta(
  status: DeploymentStatus | string
): StatusMeta {
  return (
    (deploymentStatusMeta as Record<string, StatusMeta>)[status] ?? {
      label: String(status ?? "Unknown"),
      icon: AlertCircle,
      tone: "neutral",
    }
  );
}

export function getServiceStatusMeta(
  status: ServiceStatus | string
): StatusMeta {
  return (
    (serviceStatusMeta as Record<string, StatusMeta>)[status] ?? {
      label: String(status ?? "Unknown"),
      icon: AlertCircle,
      tone: "neutral",
    }
  );
}
